/*  Shared  */
import { Notify, Payload } from 'application/Shared/Helpers';

/*  Components  */
import { debug } from 'application/Shared/Utils';

/*  Hooks  */
// import axios from 'axios';
import { useFetchReportConfig } from 'hooks/dataAccess/Reports/reportConfig';
import { useFetchReportData } from 'hooks/dataAccess/Reports/reportData';
import { useFetchTenantSetting } from 'hooks/dataAccess/Tenant/tenantSetting';

/* YARN */
import { cloneDeep } from 'lodash';

/*  React  */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams, useSearchParams } from 'react-router';

/* Components */
import Scheduled from './Scheduled';
import { isV2Report } from './helpers';
import { ChooseReportComponent } from './internal/ChooseReportComponent';
import { ChosenReportHeaderTabs } from './internal/ChosenReportHeaderTabs';
import { HeaderActionsSetupReportOrPrint } from './internal/HeaderActionsSetupReportOrPrint';
import { LoadingModal } from './internal/LoadingModal';
import { ReportHasNoData } from './internal/ReportHasNoData';
import { SetupReportViewHeaderTabs } from './internal/SetupReportViewHeaderTabs';
import { UserSetsUpReportParams } from './internal/UserSetsUpReportParams';

const resetReport = async ({
    reportSetup,
    setIgnoreParams,
    setNoData,
    setReportSetup,
    setSetupReportView
}) => {
    const reportSetupCopy = cloneDeep(reportSetup);
    delete reportSetupCopy.reports;
    setIgnoreParams(true);
    setNoData(false);
    setReportSetup(reportSetupCopy);
    setSetupReportView(true);
};

const runReport = async ({
    clientId,
    getReportData,
    reportConfiguration,
    reportSetup: stateReportSetup,
    scheduledReport = false,
    setNoData,
    setRenderingScheduledReport,
    setReportData,
    setReportSetup,
    setRunningReport,
    setSavedReportParams,
    setSetupReportView,
    setTab,
    t
}) => {
    if (
        !stateReportSetup ||
        !Object.keys(stateReportSetup) ||
        !(Object.keys(stateReportSetup).length > 0)
    ) {
        return;
    }

    const reportSetup = stateReportSetup;

    const {
        type,
        businessOwnerSelection,
        reportSelections,
        applicationSelection,
        platformSelection,
        userSelection,
        groupSelection,
        breachSelection,
        clientSelection
    } = reportSetup;

    setRunningReport(true);

    const reportKey = Object.keys(reportConfiguration).find(
        (key) => reportConfiguration[key].type === type
    );
    const report = reportConfiguration[reportKey];

    if (report == null) {
        return;
    }

    const { checkboxes } = report;

    if (type === 'businessOwner' && businessOwnerSelection === null) {
        setRunningReport(false);
        Notify.error(t('Reports.error_select_business_owner'));
        return;
    }

    const subReports = [];
    let tabState = '';

    Object.values(checkboxes).forEach((checkbox) => {
        if (reportSelections[report.type + checkbox.type]) {
            subReports.push(checkbox);
            tabState = tabState === '' ? checkbox.type : tabState;
        }
    });

    if (Object.keys(reportSelections).length === 0) {
        setRunningReport(false);
        Notify.error(t('Reports.error_one_report'));
        return;
    }

    try {
        let foundData;
        const reportData = {};

        const params = { ...reportSetup };

        if (reportSetup.type === 'offboard') {
            if (
                !reportSetup.userSelection.length ||
                reportSetup.userSelection[0] === '0'
            ) {
                setRunningReport(false);
                Notify.error(t('Common.no_user_selected'));
                return;
            }
        }

        // For the extension code that collects the data we need for product adoption, we do the same,
        // we need to offset the start and end times by the timezone difference.
        // Can't be done in ReportBuilder or by changing reportData directly because that causes the wrong date/time to be shown to the user.
        // Therefore, let's update the dates in the params directly, then.
        // So far, this change is needed for the product adoption report. It is hard to find out if this offset is needed for another report.
        if (reportSetup.type === 'productAdoption') {
            // getTimezoneOffset returns the number of hours, so to obtain the milliseconds, one needs to multiply by 60 * 1000
            const timeOffset =
                new Date(reportSetup.startTime).getTimezoneOffset() * 60000;
            params.startTime -= timeOffset;
            params.endTime -= timeOffset;
        }

        if (Array.isArray(params.applicationSelection)) {
            params.applicationSelection =
                Payload.idString(applicationSelection);
        } else {
            params.applicationSelection = `[${applicationSelection}]`;
        }
        if (Array.isArray(params.platformSelection)) {
            params.platformSelection = Payload.idString(platformSelection);
        } else {
            params.platformSelection = `[${platformSelection}]`;
        }
        params.userSelection = Payload.idString(userSelection);
        params.groupSelection = Payload.idString(groupSelection);
        params.breachSelection = Payload.idString(breachSelection);

        if (clientSelection) {
            params.clientSelection = `[${clientSelection.join(',')}]`;
        }

        if (clientId) {
            params.platformTenantId = clientId;
        }

        // TODO: Fix - SAAS-994
        // eslint-disable-next-line no-restricted-syntax, guard-for-in
        for (const i in subReports) {
            const {
                error,
                foundData: subReportFoundData,
                reportData: subReportReportData
                // TODO: Fix - SAAS-994
                // eslint-disable-next-line no-await-in-loop
            } = await getReportData({
                params,
                reportEndpoint: subReports[i].reportEndpoint,
                t
            });

            if (error) {
                const data = error.response?.data;
                debug.e('Error getting report:', error);
                setRunningReport(false);
                if (
                    data?.message !==
                    'No Usage Found for Applications in Timeframe.'
                ) {
                    return;
                }
            }

            reportData[subReports[i].type] = subReportReportData;
            foundData = subReportFoundData || foundData;
        }

        if (!foundData) {
            setNoData(true);
        } else {
            setReportData(reportData);
            setSetupReportView(false);
            setTab(tabState);
        }
        setRenderingScheduledReport(scheduledReport);
        setReportSetup({
            ...reportSetup,
            reports: [...subReports]
        });
        setSavedReportParams(params);
        setRunningReport(false);
    } catch (e) {
        debug.e(e);
        setRunningReport(false);
    }
};

const runReportWithParameters = ({
    clientId,
    getReportData,
    reportConfiguration,
    reportParams,
    scheduledReport = true,
    setNoData,
    setRenderingScheduledReport,
    setReportData,
    setReportSetup,
    setRunningReport,
    setSavedReportParams,
    setSetupReportView,
    setTab,
    t
}) => {
    const newReportSelections = {};
    const reportSelectionsKeys = Object.keys(reportParams.reportSelections);
    reportSelectionsKeys.forEach((key) => {
        newReportSelections[reportParams.reportSelections[key]] = true;
    });
    // TODO: Fix eslint issue
    // eslint-disable-next-line no-param-reassign
    reportParams.reportSelections = newReportSelections;
    // eslint-disable-next-line no-param-reassign
    delete reportParams.reports;

    runReport({
        clientId,
        getReportData,
        reportConfiguration,
        reportSetup: reportParams,
        scheduledReport,
        setNoData,
        setRenderingScheduledReport,
        setReportData,
        setReportSetup,
        setRunningReport,
        setSavedReportParams,
        setSetupReportView,
        setTab,
        t
    });
};

/* Scheduled Reporting & Saving Scheduled Reporting */
const checkScheduledReport = ({
    clientId,
    getReportData,
    reportConfiguration,
    searchParams,
    setNoData,
    setRenderingScheduledReport,
    setReportData,
    setReportSetup,
    setRunningReport,
    setSavedReportParams,
    setSetupReportView,
    setTab,
    t
}) => {
    let reportParams = searchParams?.reportParams
        ? searchParams.reportParams
        : null;
    if (searchParams?.reportParams) {
        reportParams = atob(reportParams);
        reportParams = JSON.parse(reportParams);
        runReportWithParameters({
            clientId,
            getReportData,
            reportConfiguration,
            reportParams,
            setNoData,
            setRenderingScheduledReport,
            setReportData,
            setReportSetup,
            setRunningReport,
            setSavedReportParams,
            setSetupReportView,
            setTab,
            t
        });
    }
};

export const Reports = ({ reportType }) => {
    const [ignoreParams, setIgnoreParams] = useState(false);
    const [loadedReportConfiguration, setLoadedReportConfiguration] =
        useState(false);
    const [noData, setNoData] = useState(false);
    const [renderingScheduledReport, setRenderingScheduledReport] =
        useState(false);
    const [reportData, setReportData] = useState({});
    const [reportSetup, setReportSetup] = useState({});
    const [runningReport, setRunningReport] = useState(false);
    const [savedReportParams, setSavedReportParams] = useState(null);
    const [setupReportView, setSetupReportView] = useState(true); // Where you see all the pickers, date, apps, business owner, etc.
    const [tab, setTab] = useState('');

    const { t } = useTranslation();
    const { clientId } = useParams();
    const { search } = useLocation();

    const [searchParams] = useSearchParams();

    const urlTab = searchParams.get('tab');
    const outerTab = urlTab || 'reportSetup';

    const { reportConfiguration, loading: loadingReportConfiguration } =
        useFetchReportConfig({
            clientId,
            t
        });

    const { setting: qbrToCBR } = useFetchTenantSetting({
        clientId,
        key: 'qbrToCBR'
    });

    useEffect(() => {
        if (reportConfiguration?.length > 0 && !loadingReportConfiguration) {
            setLoadedReportConfiguration(true);
        }
    }, [loadingReportConfiguration, reportConfiguration]);

    const { getReportData } = useFetchReportData();

    useEffect(() => {
        const searchParams = Object.fromEntries(new URLSearchParams(search));
        checkScheduledReport({
            clientId,
            getReportData,
            reportConfiguration,
            searchParams,
            setNoData,
            setRenderingScheduledReport,
            setReportData,
            setReportSetup,
            setRunningReport,
            setSavedReportParams,
            setSetupReportView,
            setTab,
            t
        });
    }, [clientId, getReportData, reportConfiguration, search, t]);

    // True when Report is loaded but no data is available.
    const emptyReport = setupReportView && outerTab === 'reportSetup' && noData;

    return (
        <>
            {setupReportView && !noData && <SetupReportViewHeaderTabs />}

            {(!setupReportView || noData) && (
                <HeaderActionsSetupReportOrPrint
                    reportSetup={reportSetup}
                    resetReport={() =>
                        resetReport({
                            reportSetup,
                            setIgnoreParams,
                            setNoData,
                            setReportSetup,
                            setSetupReportView
                        })
                    }
                    savedReportParams={savedReportParams}
                    noData={emptyReport}
                />
            )}

            {/*  My reports === Scheduled reports */}
            {outerTab === 'myReports' && (
                <Scheduled
                    runReport={(reportParams, scheduledReport) =>
                        runReportWithParameters({
                            clientId,
                            getReportData,
                            reportConfiguration,
                            reportParams,
                            scheduledReport,
                            setNoData,
                            setRenderingScheduledReport,
                            setReportData,
                            setReportSetup,
                            setRunningReport,
                            setSavedReportParams,
                            setSetupReportView,
                            setTab,
                            t
                        })
                    }
                />
            )}

            {emptyReport && (
                <ReportHasNoData
                    renderingScheduledReport={renderingScheduledReport}
                    reportSetup={reportSetup}
                />
            )}

            {/* Report Setup */}
            {/* We haven't asked for data to the BE, i.e., we haven't run the report, but we have info on report names, permissions, checkboxes */}
            {/* Show the setup screen with all the pickers and the Run Report button */}
            {setupReportView &&
                outerTab === 'reportSetup' &&
                !noData &&
                loadedReportConfiguration && (
                    <UserSetsUpReportParams
                        ignoreParams={ignoreParams}
                        onChange={(newReportSetup) => {
                            setReportSetup({
                                ...newReportSetup
                            });
                        }}
                        qbrToCBR={qbrToCBR}
                        reportConfiguration={reportConfiguration}
                        reportSetup={reportSetup}
                        reportType={reportType}
                        runReport={(newReportSetup) =>
                            runReport({
                                clientId,
                                getReportData,
                                reportConfiguration,
                                reportSetup: newReportSetup || reportSetup,
                                setNoData,
                                setRenderingScheduledReport,
                                setReportData,
                                setReportSetup,
                                setRunningReport,
                                setSavedReportParams,
                                setSetupReportView,
                                setTab,
                                t
                            })
                        }
                    />
                )}

            {!isV2Report(reportType) &&
                !setupReportView &&
                reportSetup.reports &&
                !renderingScheduledReport && (
                    <ChosenReportHeaderTabs
                        handleTabChange={(_, value) => setTab(value)}
                        reportSetup={reportSetup}
                        tab={tab}
                    />
                )}

            {!setupReportView && (
                <ChooseReportComponent
                    renderingScheduledReport={renderingScheduledReport}
                    reportData={reportData}
                    reportSetup={reportSetup}
                    runReport={() =>
                        runReport({
                            clientId,
                            getReportData,
                            reportConfiguration,
                            reportSetup,
                            setNoData,
                            setRenderingScheduledReport,
                            setReportData,
                            setReportSetup,
                            setRunningReport,
                            setSavedReportParams,
                            setSetupReportView,
                            setTab,
                            t
                        })
                    }
                    tab={tab}
                />
            )}

            {runningReport && <LoadingModal runningReport={runningReport} />}
        </>
    );
};
